import { CompanyDataForm } from './company-data/CompanyDataForm';
import { CompanyDataSkeleton } from './company-data/CompanyDataSkeleton';
import { useGetCompanyData, useSaveCompanyData } from './company-data/useCompanyData';
import { ICompanyData } from '@/types/company-data.types';
import useTenantState from '@/states/tenant.state';
import useCompanyState from '@/states/company.state';
import { useCompanyProfileSteps } from '@/hooks/useCompanyProfileSteps';
import VideoPlayer from '@/components/VideoPlayer';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

const CompanyData = () => {
  const { currentTenant } = useTenantState();

  useCompanyProfileSteps();
  const { companyProfileSteps } = useCompanyState();
  const videoLink = companyProfileSteps.find(step => step.step_name.toLowerCase() === 'company data')?.video_link;

  const { data: companyData, isLoading, isError } = useGetCompanyData(currentTenant?.id);
  const saveMutation = useSaveCompanyData();

  const navigate = useNavigate();

  const handleSubmit = async (formData: Partial<ICompanyData>) => {
    if (!currentTenant?.id) {
      // Resolving quietly would read as a successful save to the form, which
      // then clears the autosave draft — losing the work on the one path where
      // nothing was ever sent. Fail loudly so the draft is kept instead.
      toast.error('Workspace aktif belum siap. Muat ulang halaman, lalu simpan lagi.');
      throw new Error('Tenant aktif belum tersedia saat menyimpan data perusahaan');
    }

    await saveMutation.mutateAsync({
      tenantId: currentTenant.id,
      payload: formData,
    });

    navigate(`/${currentTenant.slug}/company-profile`);
  };

  if (isLoading) {
    return (
      <section className="w-full">
        <div className="pt-5 px-5 pb-8">
          <CompanyDataSkeleton />
        </div>
      </section>
    );
  }

  if (isError && !companyData) {
    return (
      <section className="w-full">
        <div className="pt-5 px-5 pb-8">
          <CompanyDataForm
            key="create-form"
            onSubmit={handleSubmit}
            isSubmitting={saveMutation.isLoading}
            headerAction={videoLink ? <VideoPlayer url={videoLink} title="Company Data" /> : undefined}
          />
        </div>
      </section>
    );
  }

  return (
    <section className="w-full">
      <div className="pt-5 px-5 pb-8">
        <CompanyDataForm
          key={`form-${companyData?.id || 'new'}`}
          defaultValues={companyData || undefined}
          onSubmit={handleSubmit}
          isSubmitting={saveMutation.isLoading}
          headerAction={videoLink ? <VideoPlayer url={videoLink} title="Company Data" /> : undefined}
        />
      </div>
    </section>
  );
};

export default CompanyData;
